import { useState, useRef, useCallback, useEffect } from 'react';
import { SEARCH_DEBOUNCE_DELAY } from '../constants';
import { contentToString } from '../utils/contentHelpers';

/**
 * Custom hook for managing search functionality in the chat area
 * @param {Array} validMessages - Array of valid messages to search through
 * @returns {Object} Search state and control functions
 */
export const useSearch = (validMessages) => {
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  
  const searchInputRef = useRef(null);
  const searchDebounceRef = useRef(null);

  /**
   * Perform search across all messages
   */
  const handleSearch = useCallback(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setCurrentResultIndex(-1);
      return [];
    }
    
    const results = [];
    const lowerQuery = searchQuery.toLowerCase().trim();
    
    validMessages.forEach((message, messageIndex) => {
      if (!message.content) return;
      
      const messageContent = contentToString(message.content).toLowerCase();
      let startPos = 0;
      let pos;
      
      while ((pos = messageContent.indexOf(lowerQuery, startPos)) !== -1) {
        results.push({
          messageIndex,
          position: pos,
          text: messageContent.substr(pos, lowerQuery.length),
          id: `search-match-${messageIndex}-${results.length}`
        });
        
        startPos = pos + lowerQuery.length;
      }
    });
    
    setSearchResults(results);
    setCurrentResultIndex(results.length > 0 ? 0 : -1);
    
    return results;
  }, [searchQuery, validMessages]);

  /**
   * Scroll to a specific search result
   */
  const scrollToSearchResult = useCallback((index) => {
    if (!searchResults || searchResults.length === 0 || index < 0 || index >= searchResults.length) {
      return;
    }
    
    const result = searchResults[index];
    
    const messageEl = document.getElementById(`message-${result.messageIndex}`);
    if (!messageEl) return;
    
    // Remove previous highlights
    document.querySelectorAll('.chat-message').forEach(el => {
      el.classList.remove('search-highlight-message');
    });
    
    // Add highlight to current message
    messageEl.classList.add('search-highlight-message');
    messageEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    // Reset all search highlights to inactive state
    document.querySelectorAll('.search-highlight-active').forEach(el => {
      el.classList.remove('search-highlight-active');
      el.classList.add('search-highlight');
    });
    
    // Highlight the specific match within the message
    setTimeout(() => {
      const highlights = messageEl.querySelectorAll('.search-highlight, mark');
      const matchesInThisMessage = searchResults.filter(r => r.messageIndex === result.messageIndex);
      const indexInMessage = matchesInThisMessage.findIndex(r => r === result);
      
      if (highlights.length > 0) {
        let highlightEl;
        
        if (indexInMessage >= 0 && indexInMessage < highlights.length) {
          highlightEl = highlights[indexInMessage];
        } else if (highlights.length === 1) {
          highlightEl = highlights[0];
        } else {
          // Find the highlight that contains our search query
          for (const h of highlights) {
            if (h.textContent.toLowerCase().includes(searchQuery.toLowerCase())) {
              highlightEl = h;
              break;
            }
          }
          
          if (!highlightEl) {
            highlightEl = highlights[0];
          }
        }
        
        if (highlightEl) {
          highlightEl.classList.remove('search-highlight');
          highlightEl.classList.add('search-highlight-active');
          highlightEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 100);
  }, [searchResults, searchQuery]);

  /**
   * Navigate to the next search result
   */
  const goToNextResult = useCallback(() => {
    if (searchResults.length === 0) return;
    
    const nextIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(nextIndex);
  }, [searchResults, currentResultIndex]);

  /**
   * Navigate to the previous search result
   */
  const goToPrevResult = useCallback(() => {
    if (searchResults.length === 0) return;
    
    const prevIndex = (currentResultIndex - 1 + searchResults.length) % searchResults.length;
    setCurrentResultIndex(prevIndex);
  }, [searchResults, currentResultIndex]);

  /**
   * Close the search interface
   */
  const closeSearch = useCallback(() => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    setCurrentResultIndex(-1);
    
    // Remove all search highlights
    document.querySelectorAll('.chat-message').forEach(el => {
      el.classList.remove('search-highlight-message');
    });
  }, []);

  /**
   * Clear the search input
   */
  const clearSearchInput = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setCurrentResultIndex(-1);
    
    // Remove all search highlights
    document.querySelectorAll('.chat-message').forEach(el => {
      el.classList.remove('search-highlight-message');
    });
    
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  /**
   * Handle keyboard events in the search input
   */
  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeSearch();
    } else if (e.key === 'Enter' && !e.shiftKey && searchResults.length > 0) {
      e.preventDefault();
      goToNextResult();
    } else if (e.key === 'Enter' && e.shiftKey && searchResults.length > 0) {
      e.preventDefault();
      goToPrevResult();
    } else if (e.key === 'ArrowDown' && searchResults.length > 0) {
      e.preventDefault();
      goToNextResult();
    } else if (e.key === 'ArrowUp' && searchResults.length > 0) {
      e.preventDefault();
      goToPrevResult();
    }
  }, [closeSearch, goToNextResult, goToPrevResult, searchResults.length]);

  // Debounced search effect
  useEffect(() => {
    if (searchQuery) {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      
      setIsSearching(true);
      searchDebounceRef.current = setTimeout(() => {
        const results = handleSearch();
        setIsSearching(false);
        
        // Results handling is managed by the currentResultIndex effect
      }, SEARCH_DEBOUNCE_DELAY);
    } else {
      setSearchResults([]);
      setCurrentResultIndex(-1);
    }
    
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery, handleSearch]);

  // Scroll to result when index changes
  useEffect(() => {
    let timeoutId;
    if (currentResultIndex >= 0 && searchResults.length > 0) {
      timeoutId = window.setTimeout(() => {
        scrollToSearchResult(currentResultIndex);
      }, 50);
    }
    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [currentResultIndex, scrollToSearchResult, searchResults.length]);

  // Focus search input when search is opened
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Get the active search result
  const activeSearchResult = searchResults[currentResultIndex] || null;

  return {
    showSearch,
    setShowSearch,
    searchQuery,
    setSearchQuery,
    searchResults,
    currentResultIndex,
    isSearching,
    searchInputRef,
    goToNextResult,
    goToPrevResult,
    handleSearchKeyDown,
    clearSearchInput,
    closeSearch,
    activeSearchResult
  };
};